import { useEffect, useRef, useState } from "react";
import { Play, Pause, SkipBack, SkipForward, X, Heart, DownloadSimple, SpeakerHigh, SpeakerLow, SpeakerSlash } from "@phosphor-icons/react";
import VolumeSlider from "./VolumeSlider";
import { usePlayer } from "../context/PlayerContext";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { bgUrl } from "../lib/img";
import { styleText } from "../lib/audioStyles";

// The volume the listener chose is remembered (also across visits), so the next track starts at it
// instead of jumping back to full volume.
const VOLUME_KEY = "aa_player_volume";
const loadVolume = () => {
  try {
    const v = parseFloat(localStorage.getItem(VOLUME_KEY));
    return v >= 0 && v <= 1 ? v : 0.85;
  } catch {
    return 0.85;
  }
};

export default function MediaPlayer() {
  const { current, isPlaying, toggle, next, prev, play, close } = usePlayer();
  const { user } = useAuth();
  const containerRef = useRef(null);
  const waveRef = useRef(null);
  const [duration, setDuration] = useState(0);
  const [time, setTime] = useState(0);
  const [liked, setLiked] = useState(false);
  const [volume, setVolume] = useState(loadVolume);
  const [muted, setMuted] = useState(false);
  const isPlayingRef = useRef(isPlaying);
  isPlayingRef.current = isPlaying;
  // the track-loading effect runs only when the track changes, so it reads the latest volume from here
  const volumeRef = useRef(volume);
  volumeRef.current = volume;
  const mutedRef = useRef(muted);
  mutedRef.current = muted;

  useEffect(() => {
    if (!current || !containerRef.current) return undefined;
    let cancelled = false;
    let audioEl = null;
    if (waveRef.current) {
      waveRef.current.destroy();
      waveRef.current = null;
    }
    (async () => {
      // the waveform library is big, so it loads the first time something plays
      const [{ default: WaveSurfer }, { default: Hover }] = await Promise.all([
        import("wavesurfer.js"),
        import("wavesurfer.js/dist/plugins/hover.esm.js"),
      ]);
      if (cancelled || !containerRef.current) return;
      // A real <audio> element lets the browser start playback as soon as the
      // first chunk arrives (using Range requests), instead of WaveSurfer's
      // default behavior of downloading + fully decoding the file up front.
      audioEl = new Audio();
      audioEl.crossOrigin = "anonymous";
      audioEl.preload = "auto";
      audioEl.volume = mutedRef.current ? 0 : volumeRef.current; // a new <audio> starts at 100% — carry the chosen volume over
      audioEl.src = current.audio_url;

      const ws = WaveSurfer.create({
        container: containerRef.current,
        waveColor: "rgba(255,255,255,0.2)",
        progressColor: "#880015",
        cursorColor: "#FFFFFF",
        barWidth: 2,
        barRadius: 1,
        barGap: 2,
        height: 40,
        normalize: true,
        media: audioEl,
        plugins: [
          Hover.create({
            lineColor: "#FF8090",
            lineWidth: 2,
            labelBackground: "#880015",
            labelColor: "#FFFFFF",
            labelSize: "11px",
            labelPreferLeft: false,
          }),
        ],
      });
      waveRef.current = ws;
      ws.on("ready", () => {
        setDuration(ws.getDuration());
        if (isPlayingRef.current) ws.play();
      });
      ws.on("audioprocess", () => setTime(ws.getCurrentTime()));
      ws.on("finish", () => next());
      setLiked(!!current.liked_by_me);
    })();
    return () => {
      cancelled = true;
      if (waveRef.current) {
        waveRef.current.destroy();
        waveRef.current = null;
      }
      // wavesurfer leaves an externally supplied <audio> alone when destroyed,
      // so stop it ourselves or the previous track keeps playing underneath.
      if (audioEl) {
        audioEl.pause();
        audioEl.removeAttribute("src");
        audioEl.load();
      }
    };
    // eslint-disable-next-line
  }, [current?.id]);

  useEffect(() => {
    if (!waveRef.current) return;
    if (isPlaying) waveRef.current.play();
    else waveRef.current.pause();
  }, [isPlaying]);

  // Volume side-effect: apply whenever the slider/mute changes (or after a track loads).
  useEffect(() => {
    if (!waveRef.current) return;
    waveRef.current.setVolume(muted ? 0 : volume);
  }, [volume, muted, current?.id]);

  useEffect(() => {
    try { localStorage.setItem(VOLUME_KEY, String(volume)); } catch { /* private mode: it just isn't remembered */ }
  }, [volume]);

  if (!current) return null;

  const fmt = (t) => {
    const m = Math.floor(t / 60);
    const s = Math.floor(t % 60).toString().padStart(2, "0");
    return `${m}:${s}`;
  };

  const toggleLike = async () => {
    if (!user) return;
    const { data } = await api.post(`/audios/${current.id}/like`);
    setLiked(data.liked_by_me);
    play(data);
  };

  const downloadIt = async () => {
    api.post(`/audios/${current.id}/download`).catch(() => {});
    const safe = (current.title || "audio").replace(/[^\w\-]+/g, "_");
    const ext = (current.audio_url.split(".").pop() || "mp3").split("?")[0];
    const filename = `${safe}.${ext}`;
    try {
      const res = await fetch(current.audio_url, { credentials: "omit" });
      if (!res.ok) throw new Error("fetch failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      // Cross-origin storage blocked the fetch. Fall back to an anchor with the
      // download attribute — same tab, no popup. If the server doesn't send
      // Content-Disposition, the browser will navigate the current tab which
      // is still less disruptive than opening a popup.
      const a = document.createElement("a");
      a.href = current.audio_url;
      a.download = filename;
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
    }
  };

  return (
    <div className="media-player fixed bottom-0 left-0 right-0 z-40 glass border-t border-white/10" style={{ paddingBottom: "env(safe-area-inset-bottom)" }} data-testid="media-player">
      <div className="max-w-[1600px] mx-auto px-3 md:px-8 py-2 md:py-3 grid grid-cols-12 gap-x-3 gap-y-1 md:gap-4 items-center">
        <div className="col-span-8 md:col-span-3 order-1 flex items-center gap-3 min-w-0">
          <div
            className="w-10 h-10 md:w-12 md:h-12 rounded-sm bg-cover bg-center flex-shrink-0 border border-white/10"
            style={{ backgroundImage: bgUrl(current.cover_url, 96), backgroundColor: '#232323' }}
          />
          <div className="min-w-0">
            <div className="text-sm font-bold font-display truncate" data-testid="player-title">{current.title}</div>
            <div className="text-xs text-white/50 truncate">{current.creator_name} · <span className="uppercase text-[10px] tracking-wider">{styleText(current)}</span></div>
          </div>
        </div>

        <div className="col-span-12 md:col-span-6 order-3 md:order-2 flex items-center gap-1 md:gap-3">
          <button onClick={prev} className="text-white/70 hover:text-white p-2" data-testid="player-prev"><SkipBack size={20} weight="fill" /></button>
          <button
            onClick={toggle}
            className="w-11 h-11 rounded-full bg-[#880015] hover:bg-[#A6001A] flex items-center justify-center transition-all hover:shadow-[0_0_20px_rgba(136,0,21,0.5)]"
            data-testid="player-play-toggle"
          >
            {isPlaying ? <Pause size={18} weight="fill" /> : <Play size={18} weight="fill" />}
          </button>
          <button onClick={next} className="text-white/70 hover:text-white p-2" data-testid="player-next"><SkipForward size={20} weight="fill" /></button>
          <div className="text-[10px] font-mono text-white/50 w-10 text-right">{fmt(time)}</div>
          <div ref={containerRef} className="flex-1 waveform-container" />
          <div className="text-[10px] font-mono text-white/50 w-10">{fmt(duration)}</div>
        </div>

        <div className="col-span-4 md:col-span-3 order-2 md:order-3 flex items-center justify-end gap-0 md:gap-2">
          <div className="hidden sm:flex items-center gap-2 mr-1" data-testid="player-volume-group">
            <button
              type="button"
              onClick={() => setMuted((m) => !m)}
              className="p-1.5 text-white/60 hover:text-white transition-transform hover:scale-110 active:scale-90"
              title={muted ? "Unmute" : "Mute"}
              data-testid="player-mute"
            >
              {muted || volume === 0 ? (
                <SpeakerSlash size={16} />
              ) : volume <= 0.5 ? (
                <SpeakerLow size={16} />
              ) : (
                <SpeakerHigh size={16} />
              )}
            </button>
            <VolumeSlider
              value={muted ? 0 : volume}
              onChange={(v) => { setMuted(false); setVolume(v); }}
              className="w-20"
              testId="player-volume"
            />
          </div>
          <button onClick={toggleLike} aria-label="Like" className={`p-2 ${liked ? 'text-[#FF4060]' : 'text-white/60 hover:text-white'}`} data-testid="player-like">
            <Heart size={20} weight={liked ? "fill" : "regular"} />
          </button>
          <button onClick={downloadIt} className="p-2 text-white/60 hover:text-white" data-testid="player-download">
            <DownloadSimple size={20} />
          </button>
          <button onClick={() => { waveRef.current?.pause(); close(); }} className="p-2 text-white/40 hover:text-white" data-testid="player-close">
            <X size={18} />
          </button>
        </div>
      </div>
    </div>
  );
}
